import { type RefObject, useEffect } from "react";
import {
  extractRangeConstraints,
  getConstraintValue,
  parsePixelValue,
} from "./usePanelResize.constraints";
import type {
  FlexConstraintValue,
  UsePanelResizeParams,
} from "./usePanelResize.types";

/**
 * Logs `console.warn` when `range` looks misconfigured. Does not throw or clamp—resize still runs.
 *
 * Checks: non-negative flex-grow; pixel strings match `"Npx"`; per-panel min ≤ max when both are
 * the same kind (number↔number or px↔px); if both panels use pixel mins, whether their sum fits
 * the flex container (uses first panel’s parent size when the ref is mounted; skips if not).
 *
 * Re-runs when `range` or layout axis (`isVertical`) changes.
 */
export function useRangeConstraintValidation(
  range: UsePanelResizeParams["range"] | undefined,
  isVertical: boolean,
  firstPanelRef: RefObject<HTMLElement | null>
): void {
  useEffect(() => {
    if (!range) return;

    const validateConstraint = (
      value: FlexConstraintValue | undefined,
      label: string
    ): boolean => {
      if (value === undefined) return true;

      if (typeof value === "number") {
        if (value < 0) {
          console.warn(`[usePanelResize] ${label} should be >= 0`, value);
          return false;
        }
        return true;
      }

      const pixelValue = parsePixelValue(value);
      if (pixelValue === null) {
        console.warn(
          `[usePanelResize] Invalid pixel value for ${label}: "${value}". ` +
            `Expected format: "300px". Only numbers (flex-grow) and pixels are supported.`
        );
        return false;
      }

      if (pixelValue < 0) {
        console.warn(`[usePanelResize] ${label} should be >= 0`, value);
        return false;
      }

      return true;
    };

    const minConstraints = Array.isArray(range.min) ? range.min : [range.min];
    const maxConstraints = Array.isArray(range.max) ? range.max : [range.max];

    minConstraints.forEach((min, index) => {
      validateConstraint(min, `Panel ${index + 1} min`);
    });

    maxConstraints.forEach((max, index) => {
      validateConstraint(max, `Panel ${index + 1} max`);
    });

    const maxLength = Math.max(minConstraints.length, maxConstraints.length);
    for (let i = 0; i < maxLength; i++) {
      const min = minConstraints[i];
      const max = maxConstraints[i];

      if (min !== undefined && max !== undefined && typeof min === typeof max) {
        const minVal = getConstraintValue(min);
        const maxVal = getConstraintValue(max);

        if (minVal && maxVal && minVal.value > maxVal.value) {
          console.warn(`[usePanelResize] Panel ${i + 1}: min > max`, {
            min,
            max,
          });
        }
      }
    }

    const { firstMin, secondMin } = extractRangeConstraints(range);
    const firstMinPx = getConstraintValue(firstMin);
    const secondMinPx = getConstraintValue(secondMin);
    if (firstMinPx?.isPixel && secondMinPx?.isPixel) {
      const sumOfMins = firstMinPx.value + secondMinPx.value;
      const container = firstPanelRef.current?.parentElement;
      if (container) {
        const containerSize = isVertical
          ? container.getBoundingClientRect().height
          : container.getBoundingClientRect().width;
        if (sumOfMins > containerSize) {
          console.warn(
            `[usePanelResize] Pixel min constraints are unsatisfiable: ` +
              `sum of mins (${sumOfMins}px) exceeds container (${Math.round(containerSize)}px). ` +
              `Second panel's constraint takes priority.`
          );
        }
      }
    }
  }, [range, isVertical]);
}
